/*
  These helpers use Typescript to define the interface

  Extend the interface like a so:
  declare global {
    interface Cookies {
      exampleKey: exampleType
    }
  }
 */

type ConsentTypes = readonly (
  | "ad_storage"
  | "analytics_storage"
  | "functionality_storage"
)[];

declare global {
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Cookies {
    isAdminUser: string;
    solvariClientId: string;
    solvariCookieConsent: ConsentTypes;
  }
}

type SetCookieEvent = CustomEvent<{ name: keyof Cookies }>;

/**
 * Retrieves a typed cookie value with optional JSON parsing.
 * Type-safe based on global Cookies interface. Auto JSON-parses unless disabled.
 *
 * @param name - The cookie name (key in global Cookies interface)
 * @param parseJSON - Auto parse JSON values (default: true)
 * @returns The typed cookie value, or null if not found
 * @example
 * const clientId = getCookie('solvariClientId');
 * const raw = getCookie('solvariClientId', false);
 */
function getCookie(name: keyof Cookies, parseJSON: false): string | null;
function getCookie<Name extends keyof Cookies>(
  name: Name,
  parseJSON?: true,
): Cookies[Name] | null;
function getCookie<Name extends keyof Cookies>(name: Name, parseJSON = true) {
  const value = new RegExp(`(?:^| )${name}=([^;]*)`).exec(document.cookie)?.[1];
  if (!value) {
    return null;
  }
  if (!parseJSON) {
    return value;
  }

  try {
    return JSON.parse(value) as Cookies[Name];
  } catch (_error) {
    return value;
  }
}

/**
 * Sets a typed cookie with automatic JSON stringification and domain handling.
 * Auto-stringifies non-string values and sets domain to root. Dispatches setCookie event.
 *
 * @param options.name - Cookie name (key in global Cookies interface)
 * @param options.value - Typed value to store (null to delete)
 * @param options.expires - Expiration date string (optional)
 * @param options.maxAge - Max age in seconds (optional)
 * @example setCookie({ name: 'solvariClientId', value: 'abc123', maxAge: '31536000' });
 */
function setCookie<Name extends keyof Cookies>({
  name,
  value,
  expires,
  maxAge,
  path = "/",
}: {
  expires?: string;
  maxAge?: string;
  name: Name;
  path?: string | undefined;
  value: Cookies[Name] | null;
}) {
  const cookieParts: string[] = [];

  const stringValue = typeof value !== "string" ? JSON.stringify(value) : value;

  cookieParts.push(`${name}=${stringValue}`);
  if (expires) {
    cookieParts.push(`expires=${expires}`);
  }
  if (maxAge) {
    cookieParts.push(`max-age=${maxAge}`);
  }
  cookieParts.push(`path=${path}`);
  const domainParts = document.location.hostname.split(".");
  const domain = `${domainParts.at(-2)}.${domainParts.at(-1)}`;
  cookieParts.push(`domain=${domain}`);

  document.cookie = cookieParts.join("; ");

  const event: SetCookieEvent = new CustomEvent("setCookie", {
    detail: { name },
  });
  document.dispatchEvent(event);
}

/**
 * Deletes a cookie by setting it to expire in the past.
 *
 * @param name - The cookie name to delete
 * @param path - Optional path to match (default: "/")
 * @example deleteCookie('solvariClientId');
 */
function deleteCookie(name: keyof Cookies, path?: string) {
  setCookie({
    name,
    value: null,
    expires: "Thu, 01 Jan 1970 00:00:00 UTC",
    path,
  });
}

/**
 * Executes a callback when a cookie is initialized or already exists.
 * Calls immediately if exists, otherwise listens once for setCookie event.
 *
 * @param name - The cookie name to watch
 * @param callback - Function to call with cookie value (can be async)
 * @example
 * onInitCookie('solvariClientId', (clientId) => {
 *   initializeAnalytics(clientId);
 * });
 */
function onInitCookie<Name extends keyof Cookies>(
  name: Name,
  callback: (cookie: Cookies[Name]) => Promise<void> | void,
) {
  const cookie = getCookie(name);
  if (cookie) {
    void callback(cookie);
    return;
  }

  const abortController = new AbortController();
  const signal = abortController.signal;

  const setCookieEventListener = (({ detail }: SetCookieEvent) => {
    if (detail.name === name) {
      const eventCookie = getCookie(name);
      if (eventCookie) {
        void callback(eventCookie);
      }
      abortController.abort();
    }
  }) as EventListener;

  document.addEventListener("setCookie", setCookieEventListener, {
    passive: true,
    signal,
  });
}

/**
 * Listens for changes to a specific cookie and executes callback each time it's set.
 * Persists and fires every time cookie is set via setCookie(). Not auto-removed.
 *
 * @param name - The cookie name to watch
 * @param callback - Function to call with new cookie value
 * @example
 * onSetCookie('solvariCookieConsent', (consent) => {
 *   updateAnalyticsConsent(consent);
 * });
 */
function onSetCookie<Name extends keyof Cookies>(
  name: Name,
  callback: (cookie: Cookies[Name]) => void,
) {
  const setCookieEventListener = (({ detail }: SetCookieEvent) => {
    if (detail.name === name) {
      const eventCookie = getCookie(name);
      if (eventCookie) {
        callback(eventCookie);
      }
    }
  }) as EventListener;

  document.addEventListener("setCookie", setCookieEventListener, {
    passive: true,
  });
}

export { deleteCookie, getCookie, onInitCookie, onSetCookie, setCookie };
export type { ConsentTypes };
